import React from 'react';

import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  LegacyBox,
  ExpandMoreIcon,
  ErrorOutlineIcon,
} from '@gogame/components';
import { getIn } from '@gogame/form';
import { useCurrencyFormat } from '@gogame/intl-ts';

import { useGroup } from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Group';
import { ACTIVITY_TYPES_LABELS } from 'scenes/MarketingPlanning/MarketingPlanningWizard/constants';

import { ValidationContext } from './ValidationContext';
import { ErrorTitle, ErrorBox, ErrorText, ErrorPath } from './components';
import { ERROR_ALLOCATED_BUDGET_EXCEEDS } from './constants';

const Error = ({ error: { type, payload, activityType, path } }) => {
  const formatCurrency = useCurrencyFormat();

  switch (type) {
    case ERROR_ALLOCATED_BUDGET_EXCEEDS: {
      const budgetFormatted = formatCurrency(payload.budget);
      const budgetAllocatedFormatted = formatCurrency(payload.budgetAllocated);
      return (
        <ErrorBox align={'center'} gutter={8}>
          <ErrorOutlineIcon color={'error'} />
          <LegacyBox vertical={true}>
            <ErrorText>
              {`${ACTIVITY_TYPES_LABELS[activityType]} budget exceeds planned budget (${budgetAllocatedFormatted} / ${budgetFormatted})`}
            </ErrorText>
            <ErrorPath>{[...path].splice(1).join(' > ')}</ErrorPath>
          </LegacyBox>
        </ErrorBox>
      );
    }
    default: {
      return null;
    }
  }
};

export const ValidationErrors = () => {
  const validationErrors = React.useContext(ValidationContext);
  const { getResolveName } = useGroup();
  const { activityTypes } = getIn(validationErrors, getResolveName());

  const errors = reduce(
    activityTypes,
    (acc, { budgetAllocated }) => [...acc, ...budgetAllocated],
    []
  );

  if (isEmpty(errors)) {
    return null;
  }

  return (
    <LegacyBox vertical={true} style={{ padding: 16 }}>
      <Accordion>
        <AccordionSummary
          expandIcon={
            <ExpandMoreIcon
              color={'action'}
              data-cy-label={'expandMoreWarnings'}
            />
          }
          data-cy-controls={'validation-errors-content'}
          id={'validation-errors-header'}
        >
          <ErrorTitle>Warnings ({errors.length})</ErrorTitle>
        </AccordionSummary>
        <AccordionDetails>
          <LegacyBox vertical={true}>
            {errors.map((error, i) => (
              <Error key={i} error={error} />
            ))}
          </LegacyBox>
        </AccordionDetails>
      </Accordion>
    </LegacyBox>
  );
};
